// ============================================================
// GestorRingOverConfig.jsx — migrado al Design System v2
//
// Pantalla de configuración del dashboard de RingOver.
// Permite a Super Admin elegir qué usuarios entran al análisis.
//
// Ubicación: src/assets/componentes/GestorRingOverConfig.jsx
// Ruta:      /estadisticas/ringover/configuracion
// Acceso:    solo Super Administrador
//
// Cambios respecto al v1:
//  - Imports apuntan a ./ui (DS v2)
//  - Header sobrio (sin cursiva "Usuarios *incluidos*")
//  - 3 KPIs usando el componente KpiCard del DS v2
//  - Input con prefix Search (en lugar del wrapper manual)
//  - Pills de filtro con paleta del mockup (slate-900 activo)
//  - Paleta corporativa (#003c66 / #0066b3) vía tokens HSL
//
// SIN CAMBIOS:
//  - Toda la lógica de estado (usuarios, inclusionLocal, filtros)
//  - toggleInclusion, getInclusion, marcarTodos, desmarcarTodos
//  - guardar, descartar
//  - useMemo de stats y usuariosFiltrados
//  - Endpoints API
// ============================================================

import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import {
    fetchRingoverUsers,
    updateRingoverUsers
} from '../../peticiones_API/PeticionesRingOver';
import {
    Search, Save, Users, AlertTriangle, CheckCircle2,
    X, Loader2, ArrowLeft, UserCheck, UserX,
} from "lucide-react";

// DS v2
import {
    Button, Input, KpiCard,
    Card, CardContent, Badge,
    Avatar, Checkbox, cn,
} from './ui';

import { UsuarioContext } from '../providers/personaContext';

const GestorRingOverConfig = () => {
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const { persona } = useContext(UsuarioContext);

    const [usuarios, setUsuarios] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const [busqueda, setBusqueda] = useState("");
    const [filtroEstado, setFiltroEstado] = useState('todos'); // todos | incluidos | excluidos

    // {Id: boolean} — solo contiene los que han cambiado respecto al servidor
    const [inclusionLocal, setInclusionLocal] = useState({});

    useEffect(() => {
        const init = async () => {
            try {
                const data = await fetchRingoverUsers();
                setUsuarios(data);
            } catch (error) {
                setMensaje({ tipo: 'error', texto: 'Error al cargar los usuarios' });
            } finally {
                setCargando(false);
            }
        };
        init();
    }, []);

    const hayCambios = Object.keys(inclusionLocal).length > 0;

    const toggleInclusion = (userId) => {
        const u = usuarios.find(x => x.Id === userId);
        if (!u) return;
        const valorOriginal = u.incluir;
        const valorActual = inclusionLocal.hasOwnProperty(userId)
            ? inclusionLocal[userId]
            : valorOriginal;
        const nuevoValor = !valorActual;

        // Si vuelve al valor original, quitar del registro de cambios
        const nuevoMap = { ...inclusionLocal };
        if (nuevoValor === valorOriginal) {
            delete nuevoMap[userId];
        } else {
            nuevoMap[userId] = nuevoValor;
        }
        setInclusionLocal(nuevoMap);
        setMensaje(null);
    };

    const getInclusion = (u) =>
        inclusionLocal.hasOwnProperty(u.Id) ? inclusionLocal[u.Id] : u.incluir;

    const descartar = () => {
        setInclusionLocal({});
        setMensaje(null);
    };

    const guardar = async () => {
        setGuardando(true);
        setMensaje(null);
        try {
            const updates = Object.entries(inclusionLocal).map(([Id, incluir]) => ({
                Id: parseInt(Id, 10),
                incluir,
            }));
            await updateRingoverUsers(updates);

            setUsuarios(prev => prev.map(u =>
                inclusionLocal.hasOwnProperty(u.Id)
                    ? { ...u, incluir: inclusionLocal[u.Id] }
                    : u
            ));
            setInclusionLocal({});
            setMensaje({ tipo: 'success', texto: 'Configuración guardada correctamente' });
        } catch (error) {
            const apiMsg = error.response?.data?.message || 'Error al guardar la configuración';
            setMensaje({ tipo: 'error', texto: apiMsg });
        } finally {
            setGuardando(false);
        }
    };

    // Stats globales
    const stats = useMemo(() => {
        let incluidos = 0, excluidos = 0;
        for (const u of usuarios) {
            const inc = inclusionLocal.hasOwnProperty(u.Id) ? inclusionLocal[u.Id] : u.incluir;
            if (inc) incluidos++; else excluidos++;
        }
        return { total: usuarios.length, incluidos, excluidos };
    }, [usuarios, inclusionLocal]);

    // Filtrado
    const usuariosFiltrados = useMemo(() => {
        const q = busqueda.toLowerCase().trim();
        return usuarios.filter(u => {
            if (q) {
                const matchTexto =
                    u.userName?.toLowerCase().includes(q) ||
                    u.nombreMostrado?.toLowerCase().includes(q);
                if (!matchTexto) return false;
            }
            const inc = inclusionLocal.hasOwnProperty(u.Id) ? inclusionLocal[u.Id] : u.incluir;
            if (filtroEstado === 'incluidos' && !inc) return false;
            if (filtroEstado === 'excluidos' && inc) return false;
            return true;
        });
    }, [usuarios, busqueda, filtroEstado, inclusionLocal]);

    // Acciones masivas
    const marcarTodos = () => {
        const nuevo = {};
        usuarios.forEach(u => {
            if (!u.incluir) nuevo[u.Id] = true;
        });
        setInclusionLocal(nuevo);
        setMensaje(null);
    };
    const desmarcarTodos = () => {
        const nuevo = {};
        usuarios.forEach(u => {
            if (u.incluir) nuevo[u.Id] = false;
        });
        setInclusionLocal(nuevo);
        setMensaje(null);
    };

    if (cargando) {
        return (
            <div className="bg-slate-50 flex items-center justify-center py-20">
                <div className="text-center text-slate-500">
                    <Loader2 className="size-7 text-[hsl(var(--brand-light))] mx-auto animate-spin" />
                    <div className="mt-3 text-sm">Cargando configuración…</div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">
            <div>

                {/* HEADER */}
                <div className="mb-5">
                    <button
                        onClick={() => navigate('/estadisticas/ringover')}
                        className="text-[11px] text-slate-500 hover:text-slate-900 mb-2 inline-flex items-center gap-1 transition-colors"
                    >
                        <ArrowLeft className="size-3" />
                        Volver al dashboard
                    </button>
                    <h1 className="text-xl font-semibold text-slate-900 leading-tight">
                        Configuración · Usuarios incluidos
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Selecciona qué usuarios de RingOver aparecen en el dashboard de estadísticas.
                        Los desmarcados no se cuentan en KPIs, gráficos ni en el ranking.
                    </p>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-3 gap-4 mb-5">
                    <KpiCard
                        icon={<Users className="size-3.5" />}
                        iconColor="muted"
                        label="Total usuarios"
                        value={stats.total}
                    />
                    <KpiCard
                        icon={<UserCheck className="size-3.5" />}
                        iconColor="success"
                        label="Incluidos"
                        value={stats.incluidos}
                    />
                    <KpiCard
                        icon={<UserX className="size-3.5" />}
                        iconColor="muted"
                        label="Excluidos"
                        value={stats.excluidos}
                    />
                </div>

                {/* CARD CON CONTROLES + LISTA */}
                <Card>
                    <CardContent className="p-4">
                        {/* Controles */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            {/* Buscador */}
                            <div className="flex-1 min-w-[200px]">
                                <Input
                                    placeholder="Buscar por nombre..."
                                    value={busqueda}
                                    onChange={e => setBusqueda(e.target.value)}
                                    prefix={<Search size={14} />}
                                    suffix={busqueda && (
                                        <button
                                            onClick={() => setBusqueda('')}
                                            className="p-0.5 text-slate-400 hover:text-slate-700 transition-colors"
                                            aria-label="Limpiar búsqueda"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                />
                            </div>

                            {/* Pills de filtro */}
                            <div className="flex border border-slate-300 rounded-lg overflow-hidden h-9">
                                {[
                                    { v: 'todos',     l: 'Todos' },
                                    { v: 'incluidos', l: 'Incluidos' },
                                    { v: 'excluidos', l: 'Excluidos' },
                                ].map((opt, i) => (
                                    <button
                                        key={opt.v}
                                        onClick={() => setFiltroEstado(opt.v)}
                                        className={cn(
                                            "px-3 text-xs font-medium transition-colors",
                                            i > 0 && "border-l border-slate-300",
                                            filtroEstado === opt.v
                                                ? "bg-slate-900 text-white"
                                                : "bg-white text-slate-600 hover:bg-slate-50"
                                        )}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>

                            {/* Acciones masivas */}
                            <Button variant="outline" size="sm" onClick={marcarTodos}>
                                Marcar todos
                            </Button>
                            <Button variant="outline" size="sm" onClick={desmarcarTodos}>
                                Desmarcar todos
                            </Button>
                        </div>

                        {/* Lista de usuarios */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            {usuariosFiltrados.length === 0 ? (
                                <div className="p-6 text-center text-slate-500 text-sm italic">
                                    No se han encontrado usuarios con esos filtros
                                </div>
                            ) : (
                                <div className="max-h-[500px] overflow-y-auto">
                                    {usuariosFiltrados.map((u, idx) => {
                                        const incluido = getInclusion(u);
                                        const haCambiado = inclusionLocal.hasOwnProperty(u.Id);
                                        const nombreVisible = u.nombreMostrado || u.userName;
                                        return (
                                            <label
                                                key={u.Id}
                                                className={cn(
                                                    "flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-slate-50",
                                                    idx < usuariosFiltrados.length - 1 && "border-b border-slate-100",
                                                    haCambiado && "bg-amber-50"
                                                )}
                                            >
                                                <Checkbox
                                                    checked={incluido}
                                                    onChange={() => toggleInclusion(u.Id)}
                                                />
                                                <Avatar name={nombreVisible} size="sm" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-slate-900 font-medium truncate">
                                                        {nombreVisible}
                                                    </div>
                                                    {u.nombreMostrado && u.nombreMostrado !== u.userName && (
                                                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                                                            ringover: {u.userName}
                                                        </div>
                                                    )}
                                                </div>
                                                {haCambiado && (
                                                    <Badge variant="warning">
                                                        Cambio pendiente
                                                    </Badge>
                                                )}
                                                {!incluido && !haCambiado && (
                                                    <Badge variant="secondary">
                                                        Excluido
                                                    </Badge>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="text-[11px] text-slate-500 mt-2 italic">
                            Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
                        </div>
                    </CardContent>
                </Card>

                {/* MENSAJE */}
                {mensaje && (
                    <div className={cn(
                        "mt-5 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2",
                        mensaje.tipo === 'success'
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-700"
                    )}>
                        {mensaje.tipo === 'success'
                            ? <CheckCircle2 className="size-4 shrink-0" />
                            : <AlertTriangle className="size-4 shrink-0" />
                        }
                        {mensaje.texto}
                    </div>
                )}

                {/* ACCIONES */}
                <div className="mt-5 flex justify-end items-center gap-3">
                    {hayCambios && (
                        <>
                            <span className="text-xs text-amber-600 italic">
                                {Object.keys(inclusionLocal).length} cambio(s) sin guardar
                            </span>
                            <Button variant="outline" onClick={descartar} disabled={guardando}>
                                Descartar
                            </Button>
                        </>
                    )}
                    <Button onClick={guardar} disabled={!hayCambios || guardando}>
                        {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                        {guardando ? 'Guardando…' : 'Guardar cambios'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default GestorRingOverConfig;
